import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, Check, CheckCheck, Trash2, Receipt, CreditCard, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationBell = () => {
  const { notifications, unreadCount, loading, error, markAsRead, deleteNotification, clearAll, refresh } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      console.log('[NotificationBell] Opening, refreshing...');
      refresh();
    }
    setIsOpen(!isOpen);
  };

  const getIcon = (type) => {
    if (type?.includes('expense')) return <Receipt className="w-4 h-4 text-violet-500" />;
    if (type?.includes('payment') || type?.includes('settle')) return <CreditCard className="w-4 h-4 text-emerald-500" />;
    if (type?.includes('member') || type?.includes('join')) return <UserPlus className="w-4 h-4 text-blue-500" />;
    return <Bell className="w-4 h-4 text-muted-foreground" />;
  };

  const timeAgo = (date) => {
    const mins = Math.floor((Date.now() - new Date(date)) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins/60)}h ago`;
    return `${Math.floor(mins/1440)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button variant="ghost" size="icon" className="relative" onClick={handleToggle} data-testid="notification-bell">
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-card border rounded-lg shadow-lg z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <h3 className="font-semibold text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => markAsRead()}>
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading...
              </div>
            ) : error ? (
              <div className="p-8 text-center text-red-500">
                <p className="text-sm">Error loading notifications</p>
                <p className="text-xs mt-1">{error}</p>
                <Button variant="ghost" size="sm" className="mt-2" onClick={refresh}>
                  Retry
                </Button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div key={n.id} className={`flex items-start gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-muted/50 ${!n.is_read ? 'bg-violet-500/5' : ''}`}>
                  <div className="flex-shrink-0 mt-0.5">{getIcon(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!n.is_read ? 'font-medium' : ''}`}>{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1">
                    {!n.is_read && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => markAsRead([n.id])}>
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-red-500" onClick={() => deleteNotification(n.id)}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t bg-muted/30">
              <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground hover:text-red-500" onClick={() => { clearAll(); setIsOpen(false); }}>
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Clear all
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
